import { put, call, all, takeLatest } from 'redux-saga/effects';
import { notification } from 'antd';
import get from 'lodash/get';
import {
  fetchPqtElementsSuccess,
  fetchPqtElementsFailure,
  fetchProjectPqtReportSuccess,
  fetchProjectPqtReportFailure,
  updateProjectPqtReportSuccess,
  updateProjectPqtReportFailure,
  createProjectPqtReportSuccess,
  createProjectPqtReportFailure,
  createPqtElementFailure,
  fetchDsrLogPqtSuccess,
  fetchDsrLogPqtFailure,
  updateDsrLogPqtSuccess,
  updateDsrLogPqtFailure,
} from './actions';
import {
  FETCH_PQT_ELEMENTS,
  FETCH_PROJECT_PQT_REPORT,
  UPDATE_PROJECT_PQT_REPORT,
  CREATE_PROJECT_PQT_REPORT,
  CREATE_PQT_ELEMENT,
  FETCH_DSR_LOG_PQT,
  UPDATE_DSR_LOG_PQT,
} from './types';
import axiosConfig from '../../services/axiosConfig';

function* fetchPqtElementsSaga() {
  try {
    const res = yield axiosConfig.get('/pqt-elements');
    // Support both { data: { data: { pqtElements: [] } } } and { data: { pqtElements: [] } }
    const inner = get(res, ['data', 'data'], get(res, 'data', {}));
    let pqtElements = Array.isArray(inner) ? inner : get(inner, 'pqtElements', get(res, ['data', 'pqtElements'], []));
    if (!Array.isArray(pqtElements)) {
      pqtElements = [];
    }
    pqtElements = [...pqtElements].sort((a, b) => String(a.name ?? '').localeCompare(String(b.name ?? ''), undefined, { sensitivity: 'base' }));
    if (res.status === 200) {
      yield put(fetchPqtElementsSuccess({ pqtElements }));
    } else {
      notification.error({ message: 'Something went wrong.' });
      yield put(fetchPqtElementsFailure());
    }
  } catch (error) {
    yield put(fetchPqtElementsFailure());
    notification.error({
      message: 'Failed to fetch PQT elements.',
      description: get(error, 'message', `${error}`),
    });
  }
}

function* fetchProjectPqtReportSaga({ projectId }) {
  try {
    const res = yield axiosConfig.get(`/project-pqt-reports?projectId=${projectId}`);
    const body = get(res, 'data', {});
    // API returns { projectPqtReports: [...] } or { data: { projectPqtReports: [...] } }
    const reportsArray = get(body, 'projectPqtReports') ?? get(body, ['data', 'projectPqtReports']);
    let data = null;
    if (Array.isArray(reportsArray) && reportsArray.length > 0) {
      // Use first report (latest by API order) or sort by updatedAt desc and take first
      const sorted = [...reportsArray].sort((a, b) => {
        const dateA = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
        const dateB = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
        return dateB - dateA;
      });
      data = sorted[0];
    }
    if (!data) {
      data = get(body, 'data', body);
      if (data?.projectPqtReport) data = data.projectPqtReport;
      if (Array.isArray(data) && data.length > 0) data = data[0];
    }
    if (data && typeof data === 'object' && !Array.isArray(data)) {
      // Elements use productionReportElementId (pqt element id) and bidDays
      const rawElements = data.elements ?? data.reportElements ?? data.projectPqtReportElements ?? [];
      const elements = Array.isArray(rawElements)
        ? rawElements.map((el) => {
            const elemId = el.productionReportElementId ?? el.pqtElementId ?? get(el, 'pqtElement.id') ?? el.id;
            const bidDays = el.bidDays ?? el.bidNoOfDays ?? 0;
            return {
              id: elemId,
              pqtElementId: elemId,
              bidDays: Number.isFinite(bidDays) ? bidDays : 0,
            };
          })
        : [];
      const report = {
        ...data,
        id: data.id ?? data.projectPqtReportId,
        projectPqtReportId: data.projectPqtReportId ?? data.id,
        name: data.name ?? '',
        description: data.description ?? '',
        elements,
      };
      if (res.status === 200) {
        yield put(fetchProjectPqtReportSuccess({ report }));
      } else {
        yield put(fetchProjectPqtReportFailure());
      }
    } else if (res.status === 200) {
      yield put(fetchProjectPqtReportSuccess({ report: null }));
    } else {
      yield put(fetchProjectPqtReportFailure());
    }
  } catch (error) {
    yield put(fetchProjectPqtReportSuccess({ report: null }));
  }
}

function* updateProjectPqtReportSaga({ payload }) {
  try {
    const { reportId, name, description, elements } = payload;
    const res = yield axiosConfig.put(`/project-pqt-reports/${reportId}`, {
      name: name || 'PQT Planning',
      description: description || '',
      elements: elements || [],
    });
    const response = get(res, ['data', 'data'], {});
    if (res.status === 200) {
      yield put(updateProjectPqtReportSuccess({ response }));
      notification.success({ message: 'PQT Report updated successfully.' });
    } else {
      notification.error({ message: 'Something went wrong.' });
      yield put(updateProjectPqtReportFailure());
    }
  } catch (error) {
    yield put(updateProjectPqtReportFailure());
    notification.error({
      message: 'Failed to update PQT report.',
      description: get(error, 'message', `${error}`),
    });
  }
}

function* createPqtElementSaga({ payload }) {
  try {
    const body = {
      name: payload.name || 'New Element',
      sortOrder: payload.sortOrder != null ? payload.sortOrder : 999,
    };
    const res = yield axiosConfig.post('/pqt-elements', body);
    if (res.status === 200 || res.status === 201) {
      notification.success({ message: 'PQT element created.' });
      yield put({ type: FETCH_PQT_ELEMENTS });
    } else {
      notification.error({ message: 'Failed to create element.' });
      yield put(createPqtElementFailure());
    }
  } catch (error) {
    notification.error({
      message: 'Failed to create PQT element.',
      description: get(error, 'message', `${error}`),
    });
    yield put(createPqtElementFailure());
  }
}

function* createProjectPqtReportSaga({ payload }) {
  try {
    const body = {
      name: payload.name || 'PQT Planning',
      description: payload.description || '',
      elements: payload.elements || [],
      projectId: payload.projectId,
    };
    const res = yield axiosConfig.post('/project-pqt-reports', body);
    const response = get(res, ['data', 'data'], {});
    if (res.status === 200 || res.status === 201) {
      yield put(createProjectPqtReportSuccess({ response }));
      notification.success({ message: 'PQT Report created successfully.' });
    } else {
      notification.error({ message: 'Something went wrong.' });
      yield put(createProjectPqtReportFailure());
    }
  } catch (error) {
    yield put(createProjectPqtReportFailure());
    notification.error({
      message: 'Failed to create PQT report.',
      description: get(error, 'message', `${error}`),
    });
  }
}

function* fetchDsrLogPqtSaga({ payload }) {
  try {
    const { logDate, userEmail, sowId, projectId } = payload;
    const params = new URLSearchParams({
      logDate,
      userEmail: userEmail || '',
      sowId: String(sowId),
      projectId: String(projectId),
    });
    const res = yield axiosConfig.get(`/dsr-log-pqt?${params.toString()}`);
    const body = get(res, ['data', 'data'], get(res, 'data', {}));
    const pqt = get(body, 'pqt') ?? get(body, 'Pqt') ?? null;
    const toDate = get(body, 'toDate', null);
    const elementCandidates = [
      get(body, 'elements'),
      get(body, 'Elements'),
      get(body, 'reportElements'),
      get(body, 'projectPqtReportElements'),
      get(body, 'dsrLogPqtElements'),
      get(body, 'pqt.elements'),
      get(body, 'Pqt.Elements'),
    ];
    let elements = [];
    for (let i = 0; i < elementCandidates.length; i += 1) {
      const c = elementCandidates[i];
      if (Array.isArray(c)) {
        elements = c;
        break;
      }
    }
    const normalizeDsrLogPqtElementRow = (el) => {
      if (!el || typeof el !== 'object' || Array.isArray(el)) return el;
      const nested =
        el.dsrLogPqtElement
        ?? el.dsrLogPqtReportElement
        ?? el.projectPqtReportElement
        ?? el.projectPqtReportElementDto
        ?? el.reportElement
        ?? el.element
        ?? el.pqtElementDto;
      if (nested && typeof nested === 'object') {
        return { ...nested, ...el };
      }
      return el;
    };
    elements = elements.map(normalizeDsrLogPqtElementRow);
    if (res.status === 200) {
      yield put(fetchDsrLogPqtSuccess({ pqt, elements, toDate }));
    } else {
      yield put(fetchDsrLogPqtFailure());
      notification.error({ message: 'Failed to load DSR PQT data.' });
    }
  } catch (error) {
    yield put(fetchDsrLogPqtFailure());
    notification.error({
      message: 'Failed to fetch DSR PQT.',
      description: get(error, 'message', `${error}`),
    });
  }
}

function* updateDsrLogPqtSaga({ payload }) {
  try {
    const { dsrLogId, elements } = payload;
    const requestBody = { dsrLogId, elements };
    const res = yield axiosConfig.put('/dsr-log-pqt', requestBody);
    const responseData = get(res, ['data', 'data'], get(res, 'data', {}));
    if (res.status === 200) {
      yield put(updateDsrLogPqtSuccess({ response: responseData }));
      notification.success({ message: 'PQT actual values updated successfully.' });
    } else {
      yield put(updateDsrLogPqtFailure());
      notification.error({ message: 'Failed to update PQT actual values.' });
    }
  } catch (error) {
    yield put(updateDsrLogPqtFailure());
    notification.error({
      message: 'Failed to update DSR PQT.',
      description: get(error, 'message', `${error}`),
    });
  }
}

function* projectPqtReportsWatcher() {
  yield takeLatest(FETCH_PQT_ELEMENTS, fetchPqtElementsSaga);
  yield takeLatest(FETCH_PROJECT_PQT_REPORT, fetchProjectPqtReportSaga);
  yield takeLatest(UPDATE_PROJECT_PQT_REPORT, updateProjectPqtReportSaga);
  yield takeLatest(CREATE_PROJECT_PQT_REPORT, createProjectPqtReportSaga);
  yield takeLatest(CREATE_PQT_ELEMENT, createPqtElementSaga);
  yield takeLatest(FETCH_DSR_LOG_PQT, fetchDsrLogPqtSaga);
  yield takeLatest(UPDATE_DSR_LOG_PQT, updateDsrLogPqtSaga);
}

export default function* projectPqtReportsSagas() {
  yield all([call(projectPqtReportsWatcher)]);
}
